/**
 * «Is this card drawn in the chat, under an answer, rather than on the board?»
 *
 * `chat/TurnCards.tsx` draws a turn's SNAPSHOTTED cards (`peterhof_orch`,
 * `additional_kwargs.pa_turn_cards`) and reuses the catalogue's own components
 * where they fit the chat column. One thing about a catalogue component
 * changes there, and it is about a button rather than a picture: `CardActions`
 * sends a tap with its card's id as `a2ui_action.sourceComponentId`, and a
 * snapshot may stand under an answer long after that id was replaced or taken
 * off the board. An action naming a card that is no longer there is a dead
 * button, so inline actions are drawn only while the card is still on the
 * board (`isOnBoard`), and the card is read-only after that.
 *
 * The context defaults to `false`, which is every card on the board and every
 * graph but `peterhof_orch`: nothing about them changes.
 *
 * **A board-less graph (`peterhof_agent`, #132) has no board to ask**, so the
 * same question is answered by the thread: a card under an answer is live when
 * that answer holds the NEWEST snapshot of its id (`LatestSnapshotContext`,
 * filled by `chat/TurnCards.tsx` from `inlineTurnCards.latestSnapshots`). The
 * same rule gates a plan stop's `✕` (`PlanTimeline`): yesterday's plan under an
 * old answer is read-only, or a tap on it would edit today's.
 */

import { createContext, useContext } from 'react';
import { REGION_IDS, useSurface, type SurfaceState } from '../a2ui/surfaceStore';

export const InlineCardContext = createContext(false);

export function useInlineCard(): boolean {
  return useContext(InlineCardContext);
}

/**
 * Is `id` a card the board is showing right now? Read off the regions'
 * `children`, NOT off `components`: a removed card stays in `components` as an
 * A2UI orphan (`surfaceStore.ts`), so its presence there says nothing.
 */
export function isOnBoard(state: Pick<SurfaceState, 'childrenOf'>, id: string): boolean {
  return Object.values(REGION_IDS).some((region) => state.childrenOf(region).includes(id));
}

/**
 * Under an answer on a board-less graph: is this the newest snapshot of its
 * id? `null` — not decided by the thread (the board itself, `peterhof_orch`).
 */
export const LatestSnapshotContext = createContext<boolean | null>(null);

/**
 * May this card's buttons still act? On the board — always. Under an answer —
 * the thread's word when it has one (board-less graph), else the board's.
 */
export function cardIsLive(inline: boolean, latest: boolean | null, onBoard: () => boolean): boolean {
  if (!inline) return true;
  return latest ?? onBoard();
}

export function useCardLive(id: string): boolean {
  const inline = useInlineCard();
  const latest = useContext(LatestSnapshotContext);
  return useSurface((state) => cardIsLive(inline, latest, () => isOnBoard(state, id)));
}
